/**
 * The browser's half of the workspace relay (spec 0147 §5): one operation envelope per call, the platform's typed
 * refusals told apart from the bridge's replies by the `X-Workspace-Response` header, and sends that survive reloads
 * in `localStorage` until the workspace acknowledges them with their original `op_id`.
 */
import { z } from 'zod'
import { validateOperation, isUserMessage, type UserMessage } from './agent-contract'
import { platformRefusal, WORKSPACE_RESPONSE_HEADER, type PlatformRefusal, type WorkspaceOperation } from './contract'
import { openedSession } from './chat'

export type OpsOutcome =
  | { source: 'bridge'; status: number; response: Response }
  | { source: 'platform'; refusal: PlatformRefusal }

const WAKE_RETRY_MS = 2000

export const pendingSend = z
  .object({
    session_id: z.string().regex(/^[A-Za-z0-9_-]{1,200}$/),
    message: z.custom<UserMessage>(isUserMessage),
    context: z.object({ coworld: z.string().nullable(), league: z.string().nullable() }).catchall(z.json()),
    instructions: z.string().max(64_000).optional(),
    open_id: z.uuid().optional(),
    model: z.string().optional(),
    effort: z.string().optional(),
  })
  .strict()
export type PendingSend = z.infer<typeof pendingSend>

export type PendingStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>

export const pendingKey = (workspaceId: string, opId: string) => `ide:${workspaceId}:pending:${opId}`

/** Every key this workspace owns in the browser: pending sends, remembered threads, any other cache. */
function workspaceKeys(storage: PendingStorage, workspaceId: string) {
  const prefix = `ide:${workspaceId}:`
  const keys: string[] = []
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index)
    if (key?.startsWith(prefix)) keys.push(key)
  }
  return keys
}

export function pendingSends(storage: PendingStorage, workspaceId: string) {
  const prefix = pendingKey(workspaceId, '')
  return workspaceKeys(storage, workspaceId)
    .filter((key) => key.startsWith(prefix))
    .flatMap((key) => {
      const parsed = pendingSend.safeParse(JSON.parse(storage.getItem(key)!))
      if (!parsed.success) {
        storage.removeItem(key)
        return []
      }
      return [{ opId: key.slice(prefix.length), send: parsed.data }]
    })
}

/** A retired workspace id is unknown to everyone; nothing cached under it can be shown or retried. */
export function forgetWorkspace(storage: PendingStorage, workspaceId: string) {
  for (const key of workspaceKeys(storage, workspaceId)) storage.removeItem(key)
}

const bridgeFailure = z.object({ kind: z.string(), error: z.string().optional(), name: z.string().optional() })
const inferenceFailure = z.object({
  kind: z.literal('inference_unavailable'),
  reason: z.enum(['budget_exhausted', 'provider_unavailable']).nullable(),
  until: z.iso.datetime({ offset: true }).nullable(),
})
/** The runtime's own error body, passed through by the bridge without a kind. */
const runtimeFailure = z.object({ error: z.union([z.string(), z.object({ code: z.string() })]) })

/** A failure the relay path reports to the views, by its typed kind. */
export class RelayError extends Error {
  override readonly name = 'RelayError'
  constructor(
    readonly kind: string,
    message: string,
    readonly status?: number,
    readonly detail?: unknown
  ) {
    super(message)
  }
}

/** Refused message bytes cannot succeed unchanged, so no outbox should retry them automatically. */
export function isPermanentSendFailure(error: unknown): error is RelayError {
  return (
    error instanceof RelayError &&
    ['invalid', 'unsupported_operation', 'too_large', 'scope_mismatch'].includes(error.kind)
  )
}

export interface OpsClientOptions {
  storage: PendingStorage
  operation: (
    workspaceId: string,
    operation: WorkspaceOperation,
    init: { signal?: AbortSignal | undefined; accept?: string }
  ) => Promise<Response>
  wait?: (ms: number, signal?: AbortSignal) => Promise<void>
  /** The platform answered 410 for this id: every cache keyed by it has been dropped. */
  onRetired?: (workspaceId: string) => void
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort)
      resolve()
    }, ms)
    const abort = () => {
      clearTimeout(timer)
      reject(signal!.reason)
    }
    signal?.addEventListener('abort', abort, { once: true })
  })

export class OpsClient {
  readonly options: OpsClientOptions
  private wait: (ms: number, signal?: AbortSignal) => Promise<void>
  /** Sends this page still has on the wire, by op id: a resume never races a send that is in its wake loop. */
  private readonly inFlight = new Map<
    string,
    Promise<{ requestId: string; run_id: string | null; session_id: string }>
  >()
  constructor(options: OpsClientOptions) {
    this.options = options
    this.wait = options.wait ?? sleep
  }

  /** One relay call. Platform refusals come back typed; a bridge reply comes back as the response to read. */
  async call(
    workspaceId: string,
    operation: WorkspaceOperation,
    init: { signal?: AbortSignal | undefined; accept?: string } = {}
  ): Promise<OpsOutcome> {
    validateOperation(operation)
    const request = this.options.operation(workspaceId, operation, init)
    const response = await request.catch((error: unknown) => {
      if (init.signal?.aborted) throw error
      throw new RelayError(
        'transport_unavailable',
        'The connection to your workspace was lost. Your request has not been discarded.'
      )
    })
    if (response.headers.get(WORKSPACE_RESPONSE_HEADER) === 'bridge')
      return { source: 'bridge', status: response.status, response }
    const body: unknown = response.headers.get('content-type')?.includes('application/json')
      ? await response.json()
      : await response.text().then(() => null)
    const refusal = platformRefusal.safeParse(body)
    if (refusal.success) {
      if (refusal.data.kind === 'workspace_retired') {
        forgetWorkspace(this.options.storage, workspaceId)
        this.options.onRetired?.(workspaceId)
      }
      return { source: 'platform', refusal: refusal.data }
    }
    // The IDE server's own answer (unauthenticated, invalid_request) or a proxy failure in front of it.
    const own = bridgeFailure.safeParse(body)
    throw new RelayError(
      own.success ? own.data.kind : response.status === 401 ? 'unauthenticated' : 'transport_unavailable',
      own.success
        ? (own.data.error ?? own.data.kind)
        : `The workspace relay was unavailable (HTTP ${response.status}). Reconnect to check the original request.`,
      response.status
    )
  }

  /**
   * A JSON reply from the bridge, parsed with `schema`. The workspace is woken by the call itself: `waking` and
   * `starting` are retried at the platform’s wake interval; the other refusals and the bridge's typed failures throw.
   */
  async json<T>(
    workspaceId: string,
    operation: WorkspaceOperation,
    schema: z.ZodType<T>,
    signal?: AbortSignal
  ): Promise<T> {
    const outcome = await this.settled(workspaceId, operation, { signal })
    if (outcome.source === 'platform') throw refusalError(outcome.refusal)
    const body: unknown = await outcome.response.json()
    if (outcome.status >= 200 && outcome.status < 300) return schema.parse(body)
    throw bridgeError(outcome.status, body)
  }

  /** A streamed bridge reply (`run.events`): the open response, or a typed error. */
  async stream(workspaceId: string, operation: WorkspaceOperation, signal?: AbortSignal): Promise<Response> {
    const outcome = await this.settled(workspaceId, operation, { signal, accept: 'text/event-stream' })
    if (outcome.source === 'platform') throw refusalError(outcome.refusal)
    if (outcome.status >= 200 && outcome.status < 300) return outcome.response
    throw bridgeError(outcome.status, await outcome.response.json())
  }

  /**
   * A `session.message` that is kept until the workspace acknowledges it: saved before the first attempt, retried with
   * the same `op_id` through `waking`, removed on acknowledgement. The acknowledgement is the native run id.
   */
  send(
    workspaceId: string,
    send: PendingSend,
    opId: string = crypto.randomUUID(),
    signal?: AbortSignal
  ): Promise<{ requestId: string; run_id: string | null; session_id: string }> {
    // The same op id is one delivery per page; a second tab may still send it, and only Hermes's receipt under
    // `Idempotency-Key: <op_id>` makes that harmless.
    const started =
      this.inFlight.get(opId) ??
      this.deliver(workspaceId, send, opId, signal).catch((error: unknown) => {
        if (isPermanentSendFailure(error)) this.discard(workspaceId, opId)
        throw error
      })
    this.inFlight.set(opId, started)
    return started.finally(() => {
      if (this.inFlight.get(opId) === started) this.inFlight.delete(opId)
    })
  }

  /** Persist before a surface resumes compute; delivery keeps these same operation bytes. */
  retain(workspaceId: string, send: PendingSend, opId: string) {
    const retained =
      pendingSends(this.options.storage, workspaceId).find((entry) => entry.opId === opId)?.send ??
      pendingSend.parse(send)
    this.options.storage.setItem(pendingKey(workspaceId, opId), JSON.stringify(retained))
    return retained
  }

  /** Discard only an operation the relay permanently refused, retaining every uncertain send. */
  discard(workspaceId: string, opId: string) {
    this.options.storage.removeItem(pendingKey(workspaceId, opId))
  }

  private async deliver(
    workspaceId: string,
    send: PendingSend,
    opId: string,
    signal?: AbortSignal
  ): Promise<{ requestId: string; run_id: string | null; session_id: string }> {
    const key = pendingKey(workspaceId, opId)
    send = this.retain(workspaceId, send, opId)
    if (send.open_id) {
      const opened = await this.json(
        workspaceId,
        {
          op_id: send.open_id,
          name: 'session.open',
          args: {
            client: { id: 'ide-next', kind: 'web' },
            coworld: send.context.coworld,
            league: send.context.league,
            message_op_id: opId,
          },
        },
        openedSession,
        signal
      )
      const { open_id: _, ...message } = send
      send = { ...message, session_id: opened.session_id }
      this.options.storage.setItem(key, JSON.stringify(send))
    }
    const outcome = await this.settled(
      workspaceId,
      {
        op_id: opId,
        name: 'session.message',
        args: {
          session_id: send.session_id,
          coworld: send.context.coworld,
          league: send.context.league,
          message: send.message,
          context: send.context,
          ...(send.instructions ? { instructions: send.instructions } : {}),
        },
      },
      { signal }
    )
    if (outcome.source === 'platform') throw refusalError(outcome.refusal)
    const body: unknown = await outcome.response.json()
    if (outcome.status < 200 || outcome.status >= 300) throw bridgeError(outcome.status, body)
    const admitted = z.object({ run_id: z.string().nullable(), session_id: z.string() }).parse(body)
    this.options.storage.removeItem(key)
    return { requestId: opId, ...admitted }
  }

  /** Sends saved before a reload or a sleep, delivered again with their original ids; sends on the wire are left alone. */
  async resume(workspaceId: string, signal?: AbortSignal) {
    for (const { opId, send } of pendingSends(this.options.storage, workspaceId)) {
      await (this.inFlight.get(opId) ?? this.send(workspaceId, send, opId, signal)).catch((error: unknown) => {
        if (!(error instanceof RelayError)) throw error
      })
    }
  }

  /** The same envelope again every 30 seconds while the platform reports the workspace is coming up. */
  private async settled(
    workspaceId: string,
    operation: WorkspaceOperation,
    init: { signal?: AbortSignal | undefined; accept?: string }
  ): Promise<OpsOutcome> {
    for (;;) {
      const outcome = await this.call(workspaceId, operation, init)
      if (outcome.source === 'bridge') return outcome
      const { refusal } = outcome
      if (refusal.kind !== 'waking' && refusal.kind !== 'starting') return outcome
      await this.wait(
        refusal.kind === 'waking' ? Math.max(WAKE_RETRY_MS, refusal.retry_after_ms) : WAKE_RETRY_MS,
        init.signal
      )
    }
  }
}

/** A platform refusal as the error the views show. */
export function refusalError(refusal: PlatformRefusal) {
  switch (refusal.kind) {
    case 'waking':
      return new RelayError('waking', 'Your workspace is waking up. Your request is kept and will be delivered.')
    case 'starting':
      return new RelayError('starting', `Your workspace is starting: ${refusal.detail}`)
    case 'paused':
      return new RelayError('paused', `Your workspace is paused: ${refusal.detail}`)
    case 'workspace_retired':
      return new RelayError('workspace_retired', 'This workspace was reset.', 410)
    case 'unknown_workspace':
      return new RelayError('unknown_workspace', 'Your workspace was not found.', 404)
    case 'legacy_backend':
      return new RelayError('legacy_backend', 'This workspace is on the previous system. Open /ide-final.', 409)
    case 'too_large':
      return new RelayError(
        'too_large',
        `This message is too large for the workspace (limit ${refusal.limit_bytes} bytes). Shorten it and send again.`,
        413
      )
    case 'unavailable':
      return new RelayError(
        'transport_unavailable',
        'The connection to your workspace was lost. Your request has not been discarded.',
        undefined,
        refusal.reason
      )
  }
}

/** The bridge's typed failure (`invalid`, `unsupported`, …) or the runtime's own error body passed through. */
function bridgeError(status: number, body: unknown) {
  const inference = inferenceFailure.safeParse(body)
  if (inference.success) {
    const { reason, until } = inference.data
    const retry = until ? ` Try again after ${new Date(until).toLocaleString()}.` : ''
    const message =
      reason === 'budget_exhausted'
        ? `Your workspace's model allowance is exhausted.${retry}`
        : reason === 'provider_unavailable'
          ? `The model provider is temporarily unavailable.${retry}`
          : 'Your workspace is renewing its model access. Please retry shortly.'
    return new RelayError('inference_unavailable', message, status, body)
  }
  const failure = bridgeFailure.safeParse(body)
  if (failure.success) {
    const kind = failure.data.kind === 'unsupported' ? 'unsupported_operation' : failure.data.kind
    return new RelayError(
      kind,
      failure.data.error ??
        (kind === 'unsupported_operation' ? `The workspace does not support ${failure.data.name}.` : kind),
      status,
      body
    )
  }
  const runtime = runtimeFailure.safeParse(body)
  const detail = runtime.success
    ? typeof runtime.data.error === 'string'
      ? runtime.data.error
      : runtime.data.error.code
    : `HTTP ${status}`
  return new RelayError('runtime_error', `The workspace answered: ${detail}.`, status, body)
}
